import { HttpErrorResponse } from '@angular/common/http';

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  details?: string;
  code?: string;
}

export class ErrorHandlingUtils {
  static getErrorMessage(error: any): string {
    // Handle HTTP error responses with specific status codes
    if (error instanceof HttpErrorResponse) {
      return this.getHttpErrorMessage(error);
    }
    
    // Handle API error response format from guide
    if (error?.error?.error && error?.error?.details) {
      return `${error.error.error}: ${error.error.details}`;
    }

    if (error?.error?.error) {
      return error.error.error;
    }

    if (error?.error?.details) {
      return error.error.details;
    }
    
    // Fallback to existing logic
    if (error?.message) {
      return error.message;
    }
    if (error?.error?.message) {
      return error.error.message;
    }
    return 'An unexpected error occurred';
  }

  private static getHttpErrorMessage(error: HttpErrorResponse): string {
    const status = error.status;
    // Plain-text error body (e.g. server returning a raw string) takes highest priority.
    const plainText = typeof error.error === 'string' && error.error.trim() ? error.error.trim() : null;
    const apiError = error.error as ApiErrorResponse;
    const apiMessage = plainText || apiError?.error || apiError?.message;

    // Handle specific HTTP status codes from API guide
    switch (status) {
      case 400:
        return apiMessage || 'Bad Request: Invalid input or missing required fields';
      case 403:
        return apiMessage || 'Forbidden: Insufficient permissions or user not authenticated';
      case 404:
        return apiMessage || 'Not Found: Resource does not exist';
      case 409:
        return apiMessage || 'Conflict: Resource state conflict (e.g., trying to delete running job)';
      case 422:
        return apiMessage || 'Unprocessable Entity: Valid JSON but logically invalid';
      case 500:
        return apiMessage || 'Internal Server Error: Database error or system failure';
      default:
        return apiMessage || `HTTP Error ${status}: ${error.statusText}`;
    }
  }

  static getDetailedErrorMessage(error: any): { message: string; details?: string; code?: string } {
    if (error instanceof HttpErrorResponse) {
      const apiError = error.error as ApiErrorResponse;
      return {
        message: this.getHttpErrorMessage(error),
        details: apiError?.details,
        code: apiError?.code
      };
    }
    
    if (error?.error) {
      return {
        message: error.error.error || error.error.message || 'Unknown error',
        details: error.error.details,
        code: error.error.code
      };
    }
    
    return {
      message: this.getErrorMessage(error)
    };
  }
}
